// Anchor points for pricing (original reference thresholds)
// Prices between anchors are linearly interpolated and rounded to nearest 10
const KPIR_ANCHORS = [
  [10, 212], [20, 302], [30, 363], [40, 423], [60, 515], [80, 605],
  [100, 726], [150, 907], [200, 1089], [300, 1588], [400, 2117],
  [500, 2646], [700, 3629], [1000, 4914],
];
const KPIR_EXTRA_PER_DOC = 4.54;

const RYCZALT_ANCHORS = [
  [10, 151], [20, 212], [30, 242], [40, 302], [60, 363], [80, 423],
  [100, 515], [150, 666], [200, 786], [300, 1028], [400, 1240],
  [500, 1512], [700, 1966], [1000, 2570],
];
const RYCZALT_EXTRA_PER_DOC = 3.02;

const PELNA_ANCHORS = [
  [10, 423], [20, 544], [30, 666], [40, 786], [60, 968], [80, 1149],
  [100, 1330], [150, 1573], [200, 1814], [300, 2570], [400, 3326],
  [500, 4082], [700, 5443], [1000, 7560],
];
const PELNA_EXTRA_PER_DOC = 6.05;

const EMPLOYEE_RATE = 100;
const ZUS_OWNER_RATE = 70;
const ZUS_PERSON_RATE = 70;
const PARTNER_RATE = 70;
const KOMANDYTOWA_PARTNER_RATE = 170;

const SPECIFICS_MODIFIERS = {
  foreign_transactions: { type: 'percent', value: 0.15 },
  mixed_vat: { type: 'percent', value: 0.30 },
  cash_vat: { type: 'percent', value: 0.30 },
  eu_projects: { type: 'percent', value: 0.30 },
  warehouse: { type: 'percent', value: 0.20 },
  foreign_language: { type: 'percent', value: 0.40 },
  startup: { type: 'percent', value: -0.20 },
  estonian_cit: { type: 'percent', value: 0.30 },
  team5_accounts: { type: 'percent', value: 0.30 },
  foundation: { type: 'percent', value: 0.40 },
  file_import: { type: 'percent', value: -0.10 },
  vat_refunds: { type: 'flat', value: 50 },
};

function interpolatePrice(anchors, extraPerDoc, count) {
  if (count <= 0) return anchors[0][1];
  if (count <= anchors[0][0]) return anchors[0][1];

  const last = anchors[anchors.length - 1];
  if (count > last[0]) return last[1] + (count - last[0]) * extraPerDoc;

  for (let i = 1; i < anchors.length; i++) {
    if (count <= anchors[i][0]) {
      const [prevDocs, prevPrice] = anchors[i - 1];
      const [nextDocs, nextPrice] = anchors[i];
      const ratio = (count - prevDocs) / (nextDocs - prevDocs);
      return Math.round((prevPrice + ratio * (nextPrice - prevPrice)) / 10) * 10;
    }
  }
  return last[1];
}

export function calculateBasePrice(accountingForm, documentCount) {
  switch (accountingForm) {
    case 'kpir':
      return interpolatePrice(KPIR_ANCHORS, KPIR_EXTRA_PER_DOC, documentCount);
    case 'ryczalt':
      return interpolatePrice(RYCZALT_ANCHORS, RYCZALT_EXTRA_PER_DOC, documentCount);
    case 'pelna':
      return interpolatePrice(PELNA_ANCHORS, PELNA_EXTRA_PER_DOC, documentCount);
    default:
      return 0;
  }
}

export function calculateEmployeeCost(employeeCount) {
  return employeeCount * EMPLOYEE_RATE;
}

export function calculateZusCost(formData) {
  let cost = 0;
  if (formData.zusOwner) cost += ZUS_OWNER_RATE;
  if (formData.legalEntity === 'spolka_komandytowa') {
    cost += (formData.partnerCount || 0) * KOMANDYTOWA_PARTNER_RATE;
  } else if (['spolka_cywilna', 'spolka_jawna', 'spolka_partnerska'].includes(formData.legalEntity)) {
    cost += (formData.partnerCount || 0) * PARTNER_RATE;
  }
  if (['spolka_zoo', 'spolka_akcyjna', 'spolka_komandytowo_akcyjna', 'psa', 'fundacja', 'stowarzyszenie', 'fundacja_rodzinna'].includes(formData.legalEntity)) {
    cost += (formData.zusPersonsCount || 0) * ZUS_PERSON_RATE;
  }
  return cost;
}

export function calculateSpecificsAdjustment(basePrice, specifics) {
  let percentTotal = 0;
  let flatTotal = 0;
  for (const specId of specifics) {
    const mod = SPECIFICS_MODIFIERS[specId];
    if (!mod) continue;
    if (mod.type === 'percent') percentTotal += mod.value;
    if (mod.type === 'flat') flatTotal += mod.value;
  }
  return Math.round(basePrice * percentTotal) + flatTotal;
}

const VAT_FLAT = 100;
const VAT_EU_FLAT = 80;

export function calculateVatCost(vatPayer) {
  if (!vatPayer) return 0;
  return VAT_FLAT;
}

export function calculateVatEuCost(vatEu) {
  if (!vatEu) return 0;
  return VAT_EU_FLAT;
}

export function calculateTotal(formData) {
  const basePrice = calculateBasePrice(formData.accountingForm, formData.documentCount);
  const vatCost = calculateVatCost(formData.vatPayer);
  const vatEuCost = calculateVatEuCost(formData.vatEu);
  const employeeCost = calculateEmployeeCost(formData.employeeCount);
  const zusCost = calculateZusCost(formData);
  const specificsAdjustment = calculateSpecificsAdjustment(basePrice, formData.businessSpecifics);
  const additionalServicesTotal = (formData.additionalServices || []).reduce((sum, s) => sum + s.total, 0);
  const monthlyTotal = basePrice + vatCost + vatEuCost + employeeCost + zusCost + specificsAdjustment + additionalServicesTotal;

  return {
    basePrice,
    vatCost,
    vatEuCost,
    employeeCost,
    zusCost,
    specificsAdjustment,
    additionalServicesTotal,
    monthlyTotal,
  };
}

// Legal entities compatible with each accounting form
export const ENTITY_COMPATIBILITY = {
  kpir: ['jdg', 'spolka_cywilna', 'spolka_jawna', 'spolka_partnerska'],
  ryczalt: ['jdg', 'spolka_cywilna', 'spolka_jawna', 'spolka_partnerska'],
  pelna: ['jdg', 'spolka_cywilna', 'spolka_jawna', 'spolka_partnerska', 'spolka_zoo', 'spolka_akcyjna', 'spolka_komandytowa', 'spolka_komandytowo_akcyjna', 'fundacja', 'stowarzyszenie', 'fundacja_rodzinna', 'psa'],
};

// Entities that need ZUS owner toggle
export const ZUS_OWNER_ENTITIES = ['jdg'];

// Entities that show partner count
export const PARTNER_ENTITIES = ['spolka_cywilna', 'spolka_jawna', 'spolka_partnerska', 'spolka_komandytowa'];

// Entities that show ZUS persons count
export const ZUS_PERSONS_ENTITIES = ['spolka_zoo', 'spolka_akcyjna', 'spolka_komandytowo_akcyjna', 'psa', 'fundacja', 'stowarzyszenie', 'fundacja_rodzinna'];

// Additional services catalog
export const ADDITIONAL_SERVICES = [
  { id: 'admin_fee', price: 330, unit: 'szt', pl: 'Opłata administracyjna', en: 'Administrative fee' },
  { id: 'pit_annual', price: 250, unit: 'szt', pl: 'PIT - Roczne zeznanie podatkowe właściciela', en: 'PIT - Annual tax return (owner)' },
  { id: 'ipbox_monthly', price: 200, unit: 'mies', pl: 'IP BOX - Prowadzenie ewidencji (miesięcznie)', en: 'IP BOX - Monthly records' },
  { id: 'ipbox_annual', price: 400, unit: 'szt', pl: 'IP BOX - Roczne zeznanie', en: 'IP BOX - Annual return' },
  { id: 'pit_self', price: 150, unit: 'szt', pl: 'PIT - Zestawienie do samodzielnego rozliczenia', en: 'PIT - Summary for self-filing' },
  { id: 'pit_spouse_2', price: 300, unit: 'szt', pl: 'PIT - Zeznanie właściciel + małżonek (do 2 źródeł)', en: 'PIT - Owner + spouse return (up to 2 sources)' },
  { id: 'pit_spouse_5', price: 400, unit: 'szt', pl: 'PIT - Zeznanie właściciel + małżonek (2-5 źródeł)', en: 'PIT - Owner + spouse return (2-5 sources)' },
  { id: 'pit_advance', price: 100, unit: 'szt', pl: 'Ustalenie zaliczek na PIT z US', en: 'PIT advance setup with tax office' },
  { id: 'remanent', price: 250, unit: 'godz', pl: 'REMANENT - Pomoc przy przygotowaniu', en: 'Inventory assistance' },
  { id: 'pit_correction', price: 150, unit: 'szt', pl: 'PIT KOREKTA - Korekta zeznania rocznego', en: 'PIT correction - Annual return' },
  { id: 'jpk_correction', price: 200, unit: 'mies', pl: 'JPK - Korekta JPK_VAT (za miesiąc)', en: 'JPK_VAT correction (per month)' },
  { id: 'recalc_month', price: 200, unit: 'mies', pl: 'Ponowne przeliczenie miesiąca', en: 'Monthly recalculation' },
  { id: 'late_docs', price: 150, unit: 'mies', pl: 'Dostarczenie dokumentów po 8. dniu miesiąca', en: 'Late document delivery (after 8th)' },
  { id: 'analysis', price: 250, unit: 'godz', pl: 'ANALIZY - Tabele, zestawienia, analizy', en: 'Analysis - Tables, reports' },
  { id: 'credit_app', price: 250, unit: 'godz', pl: 'WNIOSEK - Wniosek do kredytu/leasingu', en: 'Credit/leasing application' },
  { id: 'representation', price: 250, unit: 'godz', pl: 'REPREZENTACJA - Przygotowanie dok. i reprezentowanie', en: 'Representation before authorities' },
  { id: 'gus', price: 250, unit: 'godz', pl: 'GUS - Sprawozdania do GUS', en: 'GUS statistical reports' },
  { id: 'pcc', price: 250, unit: 'godz', pl: 'PCC - Deklaracja podatku od czynności cywilnoprawnych', en: 'PCC - Civil transactions tax' },
  { id: 'donation', price: 250, unit: 'godz', pl: 'DAROWIZNA - Zeznanie o nabyciu', en: 'Donation - Acquisition report' },
  { id: 'vat_r_update', price: 150, unit: 'szt', pl: 'VAT-R / NIP-8 - Aktualizacja', en: 'VAT-R / NIP-8 update' },
  { id: 'vat_r_reg', price: 250, unit: 'godz', pl: 'VAT-R - Rejestracja do VAT', en: 'VAT registration' },
  { id: 'vat_other', price: 80, unit: 'szt', pl: 'VAT-8/9M/10/11/12 - Sporządzenie deklaracji', en: 'VAT-8/9M/10/11/12 declaration' },
  { id: 'vat_26', price: 300, unit: 'szt', pl: 'VAT-26 - Sporządzenie deklaracji', en: 'VAT-26 declaration' },
  { id: 'delegation_pl', price: 40, unit: 'szt', pl: 'DELEGACJA - Krajowa (za osobę/zdarzenie)', en: 'Domestic business trip' },
  { id: 'delegation_foreign', price: 80, unit: 'szt', pl: 'DELEGACJA - Zagraniczna (za osobę/zdarzenie)', en: 'Foreign business trip' },
  { id: 'nota', price: 15, unit: 'szt', pl: 'NOTA - Wystawienie noty korygującej', en: 'Correction note' },
  { id: 'czynny_zal', price: 80, unit: 'szt', pl: 'CZYNNY ŻAL - Sporządzenie', en: 'Voluntary disclosure' },
  { id: 'letters', price: 250, unit: 'godz', pl: 'PISMA - Pisma do US/ZUS/KRS', en: 'Letters to tax/ZUS/KRS offices' },
  { id: 'certificate', price: 80, unit: 'szt', pl: 'ZAŚWIADCZENIE - O niezaleganiu ZUS/US', en: 'Non-arrears certificate' },
  { id: 'installments', price: 250, unit: 'godz', pl: 'RATY - Rozłożenie zaległości na raty', en: 'Installment arrangement' },
  { id: 'mail', price: 30, unit: 'szt', pl: 'POCZTA - Wysyłka korespondencji', en: 'Postal correspondence' },
  { id: 'vat_zd', price: 200, unit: 'szt', pl: 'VAT-ZD - Zawiadomienie', en: 'VAT-ZD notification' },
  { id: 'fixed_asset_record', price: 5, unit: 'szt', pl: 'ŚT - Ewidencja środków trwałych (za każdy)', en: 'Fixed asset record (each)' },
  { id: 'fixed_asset_new', price: 50, unit: 'szt', pl: 'ŚT - Wprowadzenie nowego środka trwałego', en: 'New fixed asset registration' },
  { id: 'depreciation', price: 250, unit: 'godz', pl: 'AMORTYZACJA - Wniosek do US (de minimis)', en: 'Depreciation de minimis application' },
  { id: 'scans', price: 3, unit: 'szt', pl: 'SKANY - Wysyłanie dokumentów skanem', en: 'Document scanning' },
  { id: 'ift_cit', price: 200, unit: 'szt', pl: 'IFT-2R / CIT-10Z - Roczna deklaracja', en: 'IFT-2R / CIT-10Z annual declaration' },
  { id: 'backlog', price: 250, unit: 'godz', pl: 'ZALEGŁOŚCI - Wyprowadzenie zaległości', en: 'Backlog resolution' },
  { id: 'archive', price: 25, unit: 'mies', pl: 'ARCHIWUM - Przechowywanie dokumentów', en: 'Document archiving (per binder/mo.)' },
  { id: 'pit_4', price: 200, unit: 'szt', pl: 'PIT-4 - Roczna deklaracja', en: 'PIT-4 annual declaration' },
  { id: 'intrastat', price: 200, unit: 'szt', pl: 'INTRASTAT - Deklaracja', en: 'INTRASTAT declaration' },
  { id: 'vat_proportion', price: 300, unit: 'szt', pl: 'PROPORCJA VAT - Roczna korekta', en: 'VAT proportion annual correction' },
  { id: 'suspension', price: 450, unit: 'szt', pl: 'ZAWIESZENIE/Likwidacja DG', en: 'Business suspension/liquidation' },
];

export { EMPLOYEE_RATE, ZUS_OWNER_RATE, ZUS_PERSON_RATE, PARTNER_RATE, KOMANDYTOWA_PARTNER_RATE, VAT_FLAT, VAT_EU_FLAT };
